import { Card, Eyebrow, SectionLabel, Text, TextField, Title2, WasabiButton } from '@kk/ui';
import { useState } from 'react';

import { Avatar, LinkButton, Status } from '@/components/bits';
import { deck } from '@/copy/deck';
import { placeholders } from '@/copy/placeholders';
import { useErrorMessage, useFulfill, useLookup, useRedemptions } from '@/data/hooks';
import { downloadCsv, toCsv } from '@/lib/csv';
import { relativeDay } from '@/lib/time';
import type { OrgRedemptionRow, RedemptionStatus } from '@/types/wire';

const CODE = /^KK-\d{4}$/;
const LIVE: ReadonlySet<RedemptionStatus> = new Set(['requested', 'held', 'confirmed']);

/** Uppercase, trimmed, and the dash inserted when a staff member types "KK4821". */
export function normalizeCode(raw: string): string {
  const s = raw.trim().toUpperCase().replace(/\s+/g, '');
  return /^KK\d{4}$/.test(s) ? `KK-${s.slice(2)}` : s;
}

function eyebrowFor(row: OrgRedemptionRow): string {
  if (LIVE.has(row.status)) return deck.redemptions.codeValid(row.code);
  const words = placeholders.redemptions.statusWord as Record<string, string | undefined>;
  return placeholders.redemptions.codeStatus(
    row.code,
    words[row.status] ?? row.status.toUpperCase(),
  );
}

function subLine(row: OrgRedemptionRow): string {
  return [
    row.assignmentTitle,
    row.gate?.scoreOk === true ? deck.redemptions.scoreGateMet : undefined,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function RedemptionsScreen() {
  const errorMessage = useErrorMessage();
  const redemptions = useRedemptions();
  const fulfill = useFulfill();
  const [code, setCode] = useState('');
  const [lookupCode, setLookupCode] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const lookup = useLookup(lookupCode);

  const lookUp = (raw: string) => {
    const c = normalizeCode(raw);
    setCode(c);
    if (!CODE.test(c)) {
      setLocalError('VALIDATION');
      setLookupCode(null);
      return;
    }
    setLocalError(null);
    setLookupCode(c);
  };

  const waiting = redemptions.data?.waiting ?? [];
  const fulfilled = redemptions.data?.fulfilled ?? [];
  const result = lookup.data;
  const lookupStatus = localError
    ? localError
    : lookupCode === null
      ? null
      : lookup.isPending
        ? placeholders.empty.loading
        : lookup.error
          ? errorMessage(lookup.error)
          : null;

  return (
    <>
      <div className="page-header">
        <div className="page-header-text">
          <Title2 tone="ink">{deck.redemptions.title}</Title2>
          <Text variant="bodySm" tone="muted">
            {deck.redemptions.subtitle}
          </Text>
        </div>
        <form
          className="actions"
          onSubmit={(e) => {
            e.preventDefault();
            lookUp(code);
          }}
        >
          <div className="actions-code">
            <TextField
              placeholder={deck.redemptions.codePlaceholder}
              value={code}
              onChangeText={(t) => setCode(t.toUpperCase())}
              autoCapitalize="characters"
              autoCorrect={false}
              accessibilityLabel={placeholders.redemptions.codeField}
              testID="code-field"
            />
          </div>
          <WasabiButton
            label={deck.redemptions.lookUp}
            size="sm"
            fullWidth={false}
            onPress={() => lookUp(code)}
            testID="look-up"
          />
        </form>
      </div>
      <div className="two-col">
        <div className="stack">
          {lookupStatus ? <Status>{lookupStatus}</Status> : null}
          {result && !lookup.error ? (
            <Card variant="playfulBold" radius="row" padding={18}>
              <div className="stack" data-testid="result-card">
                <Eyebrow tone="magenta">{eyebrowFor(result)}</Eyebrow>
                <div className="result-head">
                  <Avatar child={result.child} size={48} />
                  <div className="cell grow">
                    <Text variant="sectionHeading" tone="ink">
                      {deck.redemptions.resultTitle(result.child.firstName, result.reward.title)}
                    </Text>
                    {subLine(result) ? (
                      <Text variant="meta" tone="muted">
                        {subLine(result)}
                      </Text>
                    ) : null}
                  </div>
                </div>
                <div className="button-pair">
                  <WasabiButton
                    label={deck.redemptions.markFulfilled}
                    size="sm"
                    disabled={!LIVE.has(result.status)}
                    loading={fulfill.isPending}
                    onPress={() => void fulfill.mutateAsync(result.code).catch(() => undefined)}
                    testID="mark-fulfilled"
                  />
                  <WasabiButton
                    label={deck.redemptions.notNow}
                    variant="outline"
                    textSize="sm"
                    faceHeight={43}
                    onPress={() => {
                      setLookupCode(null);
                      setCode('');
                    }}
                  />
                </div>
                {fulfill.error ? <Status>{errorMessage(fulfill.error)}</Status> : null}
              </div>
            </Card>
          ) : null}
          <div className="under-table">
            <Text variant="meta" tone="muted">
              {deck.redemptions.fulfilledThisMonth(redemptions.data?.fulfilledThisMonth ?? 0)}
            </Text>
            <LinkButton
              label={deck.redemptions.exportCsv}
              disabled={fulfilled.length === 0}
              onPress={() => downloadCsv('redemptions.csv', toCsv(fulfilled))}
              testID="export-csv"
            />
          </div>
        </div>
        <Card variant="quiet" radius="row" padding={18}>
          <div className="stack">
            <SectionLabel>{deck.redemptions.waiting(waiting.length)}</SectionLabel>
            {redemptions.isPending ? (
              <Status>{placeholders.empty.loading}</Status>
            ) : redemptions.error ? (
              <Status>{errorMessage(redemptions.error)}</Status>
            ) : waiting.length === 0 ? (
              <Status>{placeholders.empty.waiting}</Status>
            ) : null}
            {waiting.map((r) => (
              <div className="waiting-row" key={r.id} data-testid={`waiting-${r.code}`}>
                <Avatar child={r.child} size={32} />
                <div className="cell grow">
                  <Text variant="rowTitleSm" tone="ink" numberOfLines={1}>
                    {deck.redemptions.resultTitle(r.child.firstName, r.reward.title)}
                  </Text>
                  <Text variant="meta" tone="muted" numberOfLines={1}>
                    {deck.redemptions.requested(relativeDay(r.requestedAt), r.code)}
                  </Text>
                </div>
                <LinkButton
                  label={deck.redemptions.open}
                  onPress={() => lookUp(r.code)}
                  testID={`open-${r.code}`}
                />
              </div>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}
